// Policy schedule PDF → a new policy, reviewed first (idea 31). Reads the text lines of an insurer's policy schedule
// (from @vero/pdf, cells separated by TAB — the same reader as payslips) in Greek or English and pre-fills: insurer,
// policy number, insured person, product / class, start, end and renewal dates, premium and its frequency, and the
// sum insured. The advisor checks and corrects every value BEFORE anything is saved; the PDF can be kept in the
// client's documents.
//
// Rules:
//  - nothing is invented: a value that is not on the schedule stays empty and is shown as "not found";
//  - identity numbers (ID card, passport, TIC / tax id, social insurance no.) and bank details (IBAN, account, card,
//    SWIFT / BIC) are never read: such cells are skipped and never appear in a value or a source line;
//  - generic label rules only (no insurer-specific layouts); every value keeps the line it came from;
//  - the text is untrusted input: it is only matched against fixed patterns.
import type { Changeset, Put, WorkspaceView } from './changeset.ts';
import { DomainError, clean, opt, put, touched, type Ctx } from './cmd.ts';
import { normalizeDay, type Day } from './dates.ts';
import { detectCurrency, parseAmount } from './extraction.ts';
import { newId } from './ids.ts';
import { policyCategory } from './policy-engine.ts';
import { searchKey } from './rules.ts';
import { saveDocument, type DocumentInput } from './advisory.ts';
import { savePolicy } from './services.ts';
import { PAYMENT_FREQUENCIES, type PaymentFrequency, type PolicyBranch, type PolicyCategory, type PolicyStatus } from './schema.ts';

export const SCHEDULE_KEYS = ['insurer', 'policyNumber', 'insuredName', 'product', 'startDate', 'endDate', 'renewalDate', 'premium', 'frequency', 'sumInsured'] as const;
export type ScheduleKey = typeof SCHEDULE_KEYS[number];
export interface ScheduleField { key: ScheduleKey; value: string | number; page: number; snippet: string; confidence: number; alternatives?: (string | number)[] }
export interface PolicySchedule {
  fields: Partial<Record<ScheduleKey, ScheduleField>>;
  /** category / branch suggested from the product or class wording (null when nothing matched) */
  category: PolicyCategory | null; branch: PolicyBranch | null;
  /** whether the premium read is the instalment for the frequency or the annual amount */
  premiumBasis: 'instalment' | 'annual' | null;
  currency: string;
  /** i18n keys (schedule.warn.*) */
  warnings: string[];
  /** characters of text read (0: a scan — enter the values by hand) */
  chars: number;
}

// --- never read: identity numbers and bank details ------------------------------------------------------------
// tested on the label text without accents and punctuation (norm); Greek letters are not \w, so word edges are spaces
const SENSITIVE_RE = /(?:^| )(?:ταυτοτητ\S*|α ?δ ?τ|adt|διαβατηρι\S*|α ?φ ?μ|tic|vat|iban|swift|bic|passport|identity|καρτα\S*|card|account\S*|λογαριασμ\S*|τραπεζ\S*|bank\S*|sort code|πιστωτικ\S*|χρεωστικ\S*)(?: |$)|φορολογικ\S* (?:ταυτοτ|αριθ|κωδ)|αριθμος φορολογ|tax (?:id|identification|ref|reference|number|no)|κοινωνικ\S* ασφαλισ\S* (?:αρ|no)|αρ\S* κοινωνικ|social (?:insurance|security) (?:no|number)|(?:^| )id (?:no|number|card)(?: |$)/;
const isSensitive = (s: string) => SENSITIVE_RE.test(norm(s)) || /\b[A-Z]{2}\d{2}\s?(?:[A-Z0-9]{4}\s?){3,}/.test(s) || /\b(?:\d{4}[\s-]){3}\d{4}\b/.test(s);
const IBAN = /\b[A-Z]{2}\d{2}(?:\s?[A-Z0-9]{4}){3,7}(?:\s?[A-Z0-9]{1,4})?\b/g;
const scrub = (s: string) => s.replace(IBAN, '•••').replace(/\t/g, '  ').slice(0, 160);

// --- labels (accent-free, lower case) ---------------------------------------------------------------------------
const norm = (s: string) => searchKey(s).replace(/[.:€£$%()[\]\-–—·•*_/|,;#№]/g, ' ').replace(/\s+/g, ' ').trim();
type Rule = [ScheduleKey | 'period' | 'holder', RegExp];
// order matters: specific before generic
const RULES: Rule[] = [
  ['period', /^(?:περιοδος ασφαλισης|περιοδος ισχυος|διαρκεια ασφαλισης|περιοδος|period of insurance|insurance period|policy period|period of cover|period)$/],
  ['policyNumber', /^(?:αριθμος συμβολαιου|αρ συμβολαιου|αριθ συμβολαιου|αρ συμβ|αριθμος ασφαλιστηριου|αρ ασφαλιστηριου|αριθμος πολ\S*|συμβολαιο αρ|policy (?:no|nr|number|ref|reference)|policy|certificate (?:no|number))$/],
  ['renewalDate', /^(?:ημερομηνια ανανεωσης|ανανεωση|επομενη ανανεωση|renewal date|next renewal(?: date)?|renewal)$/],
  ['startDate', /^(?:ημερομηνια εναρξης(?: ισχυος| ασφαλισης)?|εναρξη(?: ισχυος| ασφαλισης)?|ημερομηνια ισχυος|ισχυει απο|απο|start date|commencement date|date of commencement|inception date|effective date|effective from|valid from|from)$/],
  ['endDate', /^(?:ημερομηνια ληξης(?: ισχυος| ασφαλισης)?|ληξη(?: ισχυος| ασφαλισης)?|ισχυει εως|εως|expiry date|expiry|expires|expiration date|end date|maturity date|valid (?:until|to)|until|to)$/],
  ['sumInsured', /(?:ασφαλισμενο κεφαλαιο|ασφαλιζομενο κεφαλαιο|ασφαλισμενο ποσο|ασφαλιζομενο ποσο|κεφαλαιο ασφαλισης|ποσο ασφαλισης|ασφαλιστικο ποσο|ασφαλιστεα αξια|sum insured|sum assured|amount insured|insured amount|cover amount|limit of indemnity|insured value)/],
  ['frequency', /^(?:συχνοτητα πληρωμης|συχνοτητα|τροπος πληρωμης ασφαλιστρου|payment frequency|premium frequency|frequency|mode of payment|payment mode)$/],
  ['premium', /(?:ασφαλιστρο|δοση ασφαλιστρου|premium|instal?ment|total payable|amount payable|πληρωτεο ποσο)/],
  ['insuredName', /^(?:ασφαλισμενος|ασφαλισμενη|ασφαλισμενο προσωπο|ασφαλιζομενος|ασφαλιζομενη|ονομα ασφαλισμενου|ονοματεπωνυμο ασφαλισμενου|insured|insured person|insured name|name of insured|life assured|life insured|the insured)$/],
  ['holder', /^(?:αντισυμβαλλομενος|συμβαλλομενος|ληπτης(?: της)? ασφαλισης|κατοχος συμβολαιου|policyholder|policy holder|policy owner|proposer)$/],
  ['product', /^(?:προιον|ονομα προιοντος|σχεδιο|ασφαλιστικο σχεδιο|προγραμμα|ασφαλιστικο προγραμμα|κλαδος|κλαδος ασφαλισης|τυπος καλυψης|ειδος ασφαλισης|ειδος καλυψης|product|product name|plan|plan name|type of cover|cover type|cover|class of insurance|insurance class|class|type of insurance|insurance type)$/],
  ['insurer', /^(?:ασφαλιστικη εταιρεια|ασφαλιστικη|ασφαλιστης|ασφαλιστρια εταιρεια|εταιρεια|insurer|insurance company|insurers|underwriter|underwritten by|company)$/],
];
/** premium lines that are not the amount the client pays */
const NOT_PREMIUM = /φορος|τελη|δικαιωματα|tax|levy|fee|stamp|καθαρο|net premium|προμηθει|commission|κινδυνου|risk premium|εκπτωσ|discount|bonus/;
const STRONG_PREMIUM = /συνολικ|πληρωτεο|μικτο|total|payable|gross/;

const MONTH_WORDS: [RegExp, number][] = [
  [/^ιαν|^jan/, 1], [/^φεβ|^feb/, 2], [/^μαρ|^mar/, 3], [/^απρ|^apr/, 4], [/^μαι|^may/, 5], [/^ιουν|^jun/, 6],
  [/^ιουλ|^jul/, 7], [/^αυγ|^aug/, 8], [/^σεπ|^sep/, 9], [/^οκτ|^oct/, 10], [/^νοε|^nov/, 11], [/^δεκ|^dec/, 12],
];
/** every date in a value: 01/02/2026, 1.2.2026, 2026-02-01, «1 Φεβρουαρίου 2026», "1 February 2026" */
export function datesIn(text: string): Day[] {
  const out: Day[] = [];
  const re = /(\d{4}-\d{1,2}-\d{1,2})|(\d{1,2}[/.\-]\d{1,2}[/.\-]\d{4})|(\d{1,2})\s+(\p{L}{3,})\.?,?\s+(\d{4})/gu;
  for (const m of text.matchAll(re)) {
    let d: Day | null = null;
    if (m[1] || m[2]) d = normalizeDay(m[1] ?? m[2]);
    else { const w = searchKey(m[4]!); const mo = MONTH_WORDS.find(([r]) => r.test(w))?.[1]; if (mo) d = normalizeDay(`${m[5]}-${String(mo).padStart(2, '0')}-${m[3]!.padStart(2, '0')}`); }
    if (d) out.push(d);
  }
  return out;
}
/** the payment frequency named in a text (Greek or English), or null */
export function frequencyIn(text: string): PaymentFrequency | null {
  const n = searchKey(text);
  if (/εξαμην|semi.?annual|half.?year|six.?month|biannual/.test(n)) return 'semiannual';
  if (/τριμην|quarter/.test(n)) return 'quarterly';
  if (/μηνιαι|μηνιαι|monthly|per month|\bμηνα\b/.test(n)) return 'monthly';
  if (/εφαπαξ|single|one.?off|lump/.test(n)) return 'single';
  if (/ετησι|annual|yearly|per year|per annum|\bp\.?a\b/.test(n)) return 'annual';
  return null;
}
const MONEY = /(?:€|eur|£|gbp|\$|usd)?\s*-?\d{1,3}(?:[.,\s’']\d{3})*(?:[.,]\d{1,2})?(?!\d)|(?:€|eur|£|gbp|\$|usd)?\s*-?\d+(?:[.,]\d{1,2})?/gi;
function amountIn(text: string): number | null {
  // dates and percentages are not amounts
  const t = text.replace(/\d{1,2}[/.\-]\d{1,2}[/.\-]\d{4}|\d{4}-\d{1,2}-\d{1,2}/g, ' ').replace(/\d+(?:[.,]\d+)?\s*%/g, ' ');
  for (const m of t.matchAll(MONEY)) { const n = parseAmount(m[0]); if (n !== null && n > 0) return n; }
  return null;
}
const GENERAL: readonly PolicyCategory[] = ['motor', 'home', 'travel', 'liability', 'business'];
const hasLetters = (s: string, n = 2) => (s.match(/\p{L}/gu) ?? []).length >= n;

interface Cand { value: string | number; page: number; snippet: string; strong?: boolean; basis?: 'instalment' | 'annual' | null; freq?: PaymentFrequency | null }

/** Read one policy schedule. `pages`: lines per page; cells within a line separated by TAB. */
export function parsePolicySchedule(pages: string[][]): PolicySchedule {
  const out: PolicySchedule = { fields: {}, category: null, branch: null, premiumBasis: null, currency: 'EUR', warnings: [], chars: 0 };
  const all = pages.flat().join('\n');
  out.chars = all.replace(/\s/g, '').length;
  out.currency = detectCurrency(all.replace(IBAN, '')) ?? 'EUR';
  const cands = new Map<ScheduleKey | 'holder', Cand[]>();
  const push = (k: ScheduleKey | 'holder', c: Cand) => cands.set(k, [...(cands.get(k) ?? []), c]);
  let classText = '';

  pages.forEach((lines, pi) => {
    const page = pi + 1;
    for (let li = 0; li < lines.length; li++) {
      const line = (lines[li] ?? '').trim();
      if (!line || line.length > 400) continue;
      const cells = line.split('\t').map((c) => c.trim()).filter(Boolean);
      for (let ci = 0; ci < cells.length; ci++) {
        const cell = cells[ci]!;
        if (isSensitive(cell)) { if (!/:\s*\S/.test(cell)) ci++; continue; } // the label and the value next to it
        // "Label: value" in one cell, or "Label" + value in the next cell, or "Label:" at the end of a line + the next line
        const m = /^([^:]{2,60}):\s*(.*)$/.exec(cell);
        const label = m ? m[1]! : cell;
        let value = m ? m[2]!.trim() : '';
        const key = norm(label);
        const rule = RULES.find(([, re]) => re.test(key));
        if (!rule) continue;
        let snippet = cell;
        if (!value && ci + 1 < cells.length && !RULES.some(([, re]) => re.test(norm(cells[ci + 1]!.replace(/:.*$/, ''))))) { value = cells[ci + 1]!; snippet = `${cell}  ${value}`; ci++; }
        else if (!value && ci === cells.length - 1 && (m || cells.length === 1)) {
          const next = (lines[li + 1] ?? '').split('\t')[0]?.trim() ?? '';
          if (next && !isSensitive(next) && !RULES.some(([, re]) => re.test(norm(next.replace(/:.*$/, ''))))) { value = next; snippet = `${cell} ${next}`; }
        }
        if (!value || isSensitive(value)) continue;
        snippet = scrub(snippet);
        const [k] = rule;
        switch (k) {
          case 'period': {
            const ds = datesIn(value);
            if (ds[0]) push('startDate', { value: ds[0], page, snippet });
            if (ds[1]) push('endDate', { value: ds[1], page, snippet });
            break;
          }
          case 'startDate': case 'endDate': case 'renewalDate': {
            const d = datesIn(value)[0];
            if (d) push(k, { value: d, page, snippet });
            break;
          }
          case 'premium': {
            if (NOT_PREMIUM.test(key)) break;
            const n = amountIn(value);
            if (n === null) break;
            const f = frequencyIn(label) ?? frequencyIn(value);
            push('premium', { value: n, page, snippet, strong: STRONG_PREMIUM.test(key), basis: f === 'annual' ? 'annual' : f ? 'instalment' : null, freq: f });
            break;
          }
          case 'sumInsured': { const n = amountIn(value); if (n !== null) push('sumInsured', { value: n, page, snippet }); break; }
          case 'frequency': { const f = frequencyIn(value); if (f) push('frequency', { value: f, page, snippet }); break; }
          case 'policyNumber': {
            const v = value.replace(/[.,;]+$/, '').trim();
            if (/\d/.test(v) && v.length <= 40 && /^[\p{L}\d][\p{L}\d\s\-/.]*$/u.test(v)) push('policyNumber', { value: v, page, snippet });
            break;
          }
          case 'insuredName': case 'holder': case 'insurer': case 'product': {
            const v = value.replace(/\s+/g, ' ').trim().slice(0, 120);
            if (!hasLetters(v) || /\d{5,}/.test(v)) break;
            push(k, { value: v, page, snippet });
            if (k === 'product') classText += ` ${v}`;
            break;
          }
        }
      }
    }
  });
  // the insurer's name printed as a heading on page 1 (no label): "… Insurance Ltd", «… Ασφαλιστική Λτδ»
  if (!cands.get('insurer')?.length) {
    for (const line of (pages[0] ?? []).slice(0, 12)) {
      const cell = line.split('\t')[0]!.trim();
      const n = searchKey(cell);
      if (cell.length <= 60 && hasLetters(cell, 4) && !/\d{3,}/.test(cell) && !isSensitive(cell) && /(insurance|assurance|ασφαλιστικ|ασφαλειες|\blife\b)/.test(n) && /(ltd|limited|λτδ|λιμιτεδ|plc|\bsa\b|α\.?ε\.?|company|εταιρεια|insurance|ασφαλιστικ)/.test(n) && !/schedule|πινακας|συμβολαιο|policy/.test(n)) {
        push('insurer', { value: cell, page: 1, snippet: scrub(cell) });
        out.warnings.push('schedule.warn.insurer_from_heading');
        break;
      }
    }
  }
  // the insured person: the "insured" label first, the policyholder only when there is none
  if (!cands.get('insuredName')?.length && cands.get('holder')?.length) { cands.set('insuredName', cands.get('holder')!); out.warnings.push('schedule.warn.holder_as_insured'); }

  for (const key of SCHEDULE_KEYS) {
    const list = cands.get(key);
    if (!list?.length) continue;
    let pick = list[0]!;
    if (key === 'premium') pick = list.find((c) => c.strong) ?? list[0]!;
    const alts = [...new Set(list.map((c) => c.value).filter((x) => x !== pick.value))];
    out.fields[key] = { key, value: pick.value, page: pick.page, snippet: pick.snippet, confidence: alts.length ? 0.55 : key === 'insurer' && out.warnings.includes('schedule.warn.insurer_from_heading') ? 0.5 : 0.9, ...(alts.length ? { alternatives: alts } : {}) };
    if (key === 'premium') { out.premiumBasis = pick.basis ?? null; if (!out.fields.frequency && pick.freq) out.fields.frequency = { key: 'frequency', value: pick.freq, page: pick.page, snippet: pick.snippet, confidence: 0.7 }; }
  }
  if (out.fields.frequency && out.premiumBasis === null) out.premiumBasis = out.fields.frequency.value === 'annual' || out.fields.frequency.value === 'single' ? 'annual' : 'instalment';
  // category / branch from the product or class wording (only when a pattern recognises it)
  const words = classText.trim();
  if (words) {
    const a = policyCategory({ category: null, productName: words, branch: 'life' }), b = policyCategory({ category: null, productName: words, branch: 'general' });
    if (a === b) { out.category = a; out.branch = GENERAL.includes(a) ? 'general' : 'life'; }
    else if (/(?:^| )(?:ζωη\S*|ζωης|life|term life|whole life|θανατ\S*)(?: |$)/.test(norm(words))) { out.category = 'life'; out.branch = 'life'; }
  }
  if (!out.chars) out.warnings.push('schedule.warn.no_text');
  else {
    if (!out.fields.policyNumber) out.warnings.push('schedule.warn.no_number');
    if (!out.fields.premium) out.warnings.push('schedule.warn.no_premium');
    const s = out.fields.startDate?.value, e = out.fields.endDate?.value;
    if (typeof s === 'string' && typeof e === 'string' && e < s) out.warnings.push('schedule.warn.dates');
  }
  return out;
}

// --- review → policy ---------------------------------------------------------------------------------------------
export interface ScheduleValues {
  insurer?: string | null; policyNumber?: string | null; insuredName?: string | null; product?: string | null;
  branch?: PolicyBranch | null; category?: PolicyCategory | null; status?: PolicyStatus | null;
  startDate?: string | null; endDate?: string | null; renewalDate?: string | null;
  premium?: number | string | null; premiumBasis?: 'instalment' | 'annual' | null; frequency?: PaymentFrequency | null;
  sumInsured?: number | string | null; currency?: string | null;
}
/** the reviewed values as they were read (the UI starts the form from here) */
export function scheduleValues(s: PolicySchedule): ScheduleValues {
  const f = (k: ScheduleKey) => s.fields[k]?.value ?? null;
  return {
    insurer: f('insurer') as string | null, policyNumber: f('policyNumber') as string | null, insuredName: f('insuredName') as string | null, product: f('product') as string | null,
    branch: s.branch, category: s.category, status: 'active', startDate: f('startDate') as string | null, endDate: f('endDate') as string | null, renewalDate: f('renewalDate') as string | null,
    premium: f('premium') as number | null, premiumBasis: s.premiumBasis, frequency: (f('frequency') as PaymentFrequency | null) ?? null, sumInsured: f('sumInsured') as number | null, currency: s.currency,
  };
}
/** a policy with the same number already in the CRM (case and spaces ignored) */
export function policyWithNumber(v: WorkspaceView, number: string | null | undefined) {
  const k = String(number ?? '').replace(/\s+/g, '').toUpperCase();
  return k ? v.rows('policies').find((p) => String(p.policyNumber ?? '').replace(/\s+/g, '').toUpperCase() === k) : undefined;
}
const PER_YEAR: Record<PaymentFrequency, number> = { monthly: 12, quarterly: 4, semiannual: 2, annual: 1, single: 1 };
function money(x: unknown, code: string, required = false): number | null {
  if (x === null || x === undefined || x === '') { if (required) throw new DomainError(code); return null; }
  const n = typeof x === 'number' ? x : parseAmount(String(x));
  if (n === null || !Number.isFinite(n) || n < 0) throw new DomainError(code);
  return Math.round(n * 100) / 100;
}
function day(x: unknown): string | null { if (x === null || x === undefined || x === '') return null; const d = normalizeDay(x); if (!d) throw new DomainError('schedule.date'); return d; }

export interface PolicyFromScheduleInput { personId: string; values: ScheduleValues; document?: DocumentInput | null; allowDuplicateNumber?: boolean }
/**
 * Save the REVIEWED values as a new policy (and, when given, the schedule PDF as a document of the client linked to
 * it) in one changeset. The premium is required (unknown is not zero); general policies use the end date as the
 * renewal date when no renewal date is given.
 */
export function savePolicyFromSchedule(v: WorkspaceView, input: PolicyFromScheduleInput, ctx: Ctx): Changeset {
  const val = input.values;
  const product = clean(val.product);
  if (!product) throw new DomainError('schedule.product_required');
  const frequency = val.frequency ?? 'annual';
  if (!(PAYMENT_FREQUENCIES as readonly string[]).includes(frequency)) throw new DomainError('schedule.frequency');
  const premium = money(val.premium, 'schedule.premium', true)!;
  const basis = val.premiumBasis ?? (frequency === 'annual' || frequency === 'single' ? 'annual' : 'instalment');
  const annualPremium = basis === 'annual' || frequency === 'single' ? premium : Math.round(premium * PER_YEAR[frequency] * 100) / 100;
  const instalment = frequency === 'annual' || frequency === 'single' ? null : basis === 'instalment' ? premium : Math.round((premium / PER_YEAR[frequency]) * 100) / 100;
  const start = day(val.startDate), end = day(val.endDate), renewal = day(val.renewalDate);
  if (start && end && end < start) throw new DomainError('schedule.dates');
  const category = val.category ?? null;
  const branch: PolicyBranch = val.branch ?? (category && GENERAL.includes(category) ? 'general' : 'life');
  const number = opt(val.policyNumber);
  const dup = policyWithNumber(v, number);
  if (dup && !input.allowDuplicateNumber) throw new DomainError('schedule.duplicate');
  const person = v.get('persons', input.personId);
  if (!person) throw new DomainError('person.not_found');
  const insured = opt(val.insuredName);
  const policyId = newId();
  const pcs = savePolicy(v, {
    id: policyId, personId: person.id, productName: product, carrier: opt(val.insurer), policyNumber: number, branch, category, status: val.status ?? 'active',
    startDate: start, issueDate: null, renewalDate: renewal ?? (branch === 'general' ? end : null), endDate: end,
    paymentFrequency: frequency, annualPremium, instalment, currency: (opt(val.currency) ?? 'EUR').toUpperCase(), sumAssured: money(val.sumInsured, 'schedule.sum'),
    insuredName: insured && searchKey(insured) !== searchKey(person.displayName) ? insured : null,
  }, ctx);
  const puts: Put[] = pcs.puts.map((p) => (p.table === 'policies' && (p.row as { id: string }).id === policyId ? put('policies', { ...(p.row as never as Record<string, unknown>), data: { source: 'schedule_pdf' } } as never) : p));
  if (input.document) {
    // a PDF already in the client's documents is only linked to the new policy (its other fields are not touched)
    const existing = input.document.id ? v.get('documents', input.document.id) : undefined;
    if (input.document.id && (!existing || (existing.personId && existing.personId !== person.id))) throw new DomainError('document.not_found');
    let docId: string;
    if (existing) { docId = existing.id; if (!existing.policyId) puts.push(put('documents', touched(ctx, existing, { policyId, personId: existing.personId ?? person.id }))); }
    else { const dcs = saveDocument(v, { ...input.document, id: undefined, personId: person.id, policyId, category: input.document.category ?? 'policies', type: input.document.type ?? 'policy_schedule' }, ctx); puts.push(...dcs.puts); docId = dcs.entityId!; }
    const i = puts.findIndex((p) => p.table === 'policies' && (p.row as { id: string }).id === policyId);
    puts[i] = put('policies', { ...(puts[i]!.row as never as Record<string, unknown>), data: { source: 'schedule_pdf', sourceDocumentId: docId } } as never);
  }
  return { action: 'policy.from_schedule', entity: 'policies', entityId: policyId, summary: product, puts, deletes: [] };
}
